import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { 
  TrendingUp, ChevronDown, ChevronRight, Euro, Calendar,
  Check, Clock, AlertCircle, ArrowLeft, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from './components/AnwesenheitTag';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Monats-Namen für Header
const MONATE = ["", "Januar", "Februar", "März", "April", "Mai", "Juni", 
                "Juli", "August", "September", "Oktober", "November", "Dezember"];

export default function Finanzuebersicht() {
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMonat, setExpandedMonat] = useState(null);
  const [ratenAbw, setRatenAbw] = useState(0);

  useEffect(() => {
    loadDaten();
  }, [jahr]);

  useEffect(() => {
    axios.get(`${API}/admin/raten-abgleich`).then(r => setRatenAbw(r.data.anzahl || 0)).catch(() => setRatenAbw(0));
  }, []);

  const loadDaten = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/finanzuebersicht/${jahr}`);
      setDaten(response.data);
    } catch (error) {
      console.error('Fehler:', error);
      toast.error('Fehler beim Laden der Finanzdaten');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('de-DE');
    } catch {
      return dateStr;
    }
  };

  const toggleMonat = (monat) => {
    setExpandedMonat(expandedMonat === monat ? null : monat);
  };

  // Aktueller Monat für Hervorhebung
  const aktuellerMonat = new Date().getMonth() + 1;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-500">Laden...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-green-600" />
              Finanzübersicht
            </h1>
            <p className="text-gray-500">Monatliche Einnahmen und Fälligkeiten</p>
          </div>
          
          {ratenAbw > 0 && (
            <Button variant="outline" data-testid="btn-raten-abgleich" className="border-orange-400 text-orange-700"
              onClick={async () => {
                if (!window.confirm(`${ratenAbw} Rechnung(en): Raten wurden abgehakt, stehen im Ratenplan aber noch offen. Jetzt als bezahlt übernehmen? (Zahlungsdatum bleibt leer, wird protokolliert)`)) return;
                try {
                  const { data } = await axios.post(`${API}/admin/raten-abgleich/uebernehmen`);
                  toast.success(`${data.geaendert} Rechnung(en) übernommen`);
                  setRatenAbw(0);
                  loadDaten();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
              }}>
              Abgehakte Raten übernehmen ({ratenAbw})
            </Button>
          )}
          <Button variant="outline" data-testid="btn-finanz-abgleich-pdf"
            onClick={() => downloadPdf(`/admin/finanz-abgleich/${jahr}/pdf`, `Finanz-Abgleich_${jahr}.pdf`)}>
            Finanz-Abgleich {jahr} (PDF)
          </Button>
          {/* Jahr-Auswahl */}
          <div className="flex items-center gap-2 bg-white border rounded-lg p-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setJahr(jahr - 1)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="font-bold text-lg px-4">{jahr}</span>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setJahr(jahr + 1)}
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Gesamtübersicht Cards */}
      {daten?.gesamt && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Einmalzahlungen</p>
              <p className="text-xl font-bold">{formatCurrency(daten.gesamt.einmalzahlungen_erwartet)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Ratenzahlungen</p>
              <p className="text-xl font-bold">{formatCurrency(daten.gesamt.raten_erwartet)}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <p className="text-sm text-purple-600">davon AfA</p>
              <p className="text-xl font-bold text-purple-700">{formatCurrency(daten.gesamt.agentur_fuer_arbeit)}</p>
              <p className="text-xs text-purple-500">+1 Monat verzögert</p>
            </CardContent>
          </Card>
          <Card className="bg-pink-50 border-pink-200" data-testid="rabatt-gewaehrt-card">
            <CardContent className="p-4">
              <p className="text-sm text-pink-600">Rabatt gewährt</p>
              <p className="text-xl font-bold text-pink-700">{formatCurrency(daten.gesamt.rabatt_gewaehrt)}</p>
              <p className="text-xs text-pink-500">Summe aller Nachlässe</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-600">Gesamt erwartet</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency(daten.gesamt.gesamt_erwartet)}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <p className="text-sm text-green-600">Bereits bezahlt</p>
              <p className="text-xl font-bold text-green-700">{formatCurrency(daten.gesamt.bereits_bezahlt)}</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <p className="text-sm text-orange-600">Noch offen</p>
              <p className="text-xl font-bold text-orange-700" data-testid="finanz-noch-offen-gesamt">{formatCurrency(daten.gesamt.noch_offen_gesamt ?? daten.gesamt.noch_offen)}</p>
              <p className="text-xs text-orange-600" data-testid="finanz-noch-offen-jahr">alle Rechnungen · davon fällig {daten.jahr}: {formatCurrency(daten.gesamt.noch_offen)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monats-Tabelle */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Monatliche Aufstellung {jahr}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium">Monat</th>
                  <th className="text-right p-3 font-medium">Einmalzahlungen</th>
                  <th className="text-right p-3 font-medium">Raten</th>
                  <th className="text-right p-3 font-medium text-purple-600">AfA</th>
                  <th className="text-right p-3 font-medium text-blue-600">Gesamt</th>
                  <th className="text-right p-3 font-medium text-green-600">Bezahlt</th>
                  <th className="text-right p-3 font-medium text-orange-600">Offen</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {daten?.monate?.map((monat) => (
                  <React.Fragment key={monat.monat}>
                    {/* Monats-Zeile */}
                    <tr 
                      className={`border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                        monat.monat === aktuellerMonat ? 'bg-yellow-50' : ''
                      } ${monat.gesamt_erwartet === 0 ? 'text-gray-400' : ''}`}
                      onClick={() => monat.details?.length > 0 && toggleMonat(monat.monat)}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {monat.details?.length > 0 && (
                            expandedMonat === monat.monat 
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />
                          )}
                          <span className={`font-medium ${monat.monat === aktuellerMonat ? 'text-yellow-700' : ''}`}>
                            {monat.monat_name}
                          </span>
                          {monat.monat === aktuellerMonat && (
                            <Badge variant="outline" className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300">
                              Aktuell
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono">
                        {monat.einmalzahlungen_erwartet > 0 ? formatCurrency(monat.einmalzahlungen_erwartet) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono">
                        {monat.raten_erwartet > 0 ? formatCurrency(monat.raten_erwartet) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono text-purple-600">
                        {monat.agentur_fuer_arbeit > 0 ? formatCurrency(monat.agentur_fuer_arbeit) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono font-bold text-blue-600">
                        {monat.gesamt_erwartet > 0 ? formatCurrency(monat.gesamt_erwartet) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono text-green-600">
                        {monat.bereits_bezahlt > 0 ? formatCurrency(monat.bereits_bezahlt) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono text-orange-600">
                        {monat.noch_offen > 0 ? formatCurrency(monat.noch_offen) : '-'}
                      </td>
                      <td className="p-3 text-right text-gray-400 text-sm">
                        {monat.details?.length > 0 && `${monat.details.length}x`}
                      </td>
                    </tr>

                    {/* Detail-Zeilen (aufgeklappt) */}
                    {expandedMonat === monat.monat && monat.details?.length > 0 && (
                      <tr>
                        <td colSpan="8" className="p-0 bg-gray-50">
                          <div className="p-4 border-l-4 border-blue-400">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-1 px-2">Empfänger</th>
                                  <th className="text-left py-1 px-2">Rechnungs-Nr.</th>
                                  <th className="text-left py-1 px-2">Typ</th>
                                  <th className="text-right py-1 px-2">Betrag</th>
                                  <th className="text-left py-1 px-2">Fällig</th>
                                  <th className="text-center py-1 px-2">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {monat.details.map((detail, idx) => (
                                  <tr key={idx} className="border-t border-gray-200">
                                    <td className="py-2 px-2 font-medium">
                                      {detail.empfaenger}
                                      {detail.ist_bildungsgutschein && (
                                        <Badge variant="outline" className="ml-2 text-xs bg-purple-50 text-purple-600 border-purple-200">
                                          AfA
                                        </Badge>
                                      )}
                                    </td>
                                    <td className="py-2 px-2 text-gray-600">{detail.rechnungsnummer}</td>
                                    <td className="py-2 px-2">
                                      {detail.typ === 'rate' ? (
                                        <Badge variant="outline" className="text-xs">
                                          Rate {detail.rate_nr}
                                        </Badge>
                                      ) : (
                                        <Badge variant="secondary" className="text-xs">
                                          Einmal
                                        </Badge>
                                      )}
                                    </td>
                                    <td className="py-2 px-2 text-right font-mono" data-testid={`finanz-detail-betrag-${detail.rechnungsnummer}-${detail.rate_nr || 0}`}>
                                      {!detail.bezahlt && detail.offen != null && detail.offen < detail.betrag - 0.004 ? (
                                        <>
                                          <span className="text-orange-700">{formatCurrency(detail.offen)} offen</span>
                                          <span className="block text-xs text-gray-500">von {formatCurrency(detail.betrag)} · {formatCurrency(detail.betrag - detail.offen)} bezahlt</span>
                                        </>
                                      ) : formatCurrency(detail.betrag)}
                                    </td>
                                    <td className="py-2 px-2 text-gray-600">
                                      {formatDate(detail.faellig_am)}
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      {detail.bezahlt ? (
                                        <span className="inline-flex items-center gap-1 text-green-600">
                                          <Check className="h-4 w-4" />
                                          Bezahlt
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-orange-600" data-testid={`finanz-detail-status-${detail.rechnungsnummer}-${detail.rate_nr || 0}`}>
                                          <Clock className="h-4 w-4" />
                                          {detail.offen != null && detail.offen < detail.betrag - 0.004 ? 'Teilweise' : 'Offen'}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                {/* Gesamt-Zeile */}
                {daten?.gesamt && (
                  <tr className="bg-gray-100 font-bold border-t-2">
                    <td className="p-3">Gesamt {jahr}</td>
                    <td className="p-3 text-right font-mono">{formatCurrency(daten.gesamt.einmalzahlungen_erwartet)}</td>
                    <td className="p-3 text-right font-mono">{formatCurrency(daten.gesamt.raten_erwartet)}</td>
                    <td className="p-3 text-right font-mono text-purple-600">{formatCurrency(daten.gesamt.agentur_fuer_arbeit)}</td>
                    <td className="p-3 text-right font-mono text-blue-600">{formatCurrency(daten.gesamt.gesamt_erwartet)}</td>
                    <td className="p-3 text-right font-mono text-green-600">{formatCurrency(daten.gesamt.bereits_bezahlt)}</td>
                    <td className="p-3 text-right font-mono text-orange-600">{formatCurrency(daten.gesamt.noch_offen)}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legende */}
      <div className="mt-4 flex gap-6 text-sm text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-yellow-200 border border-yellow-400"></span>
          Aktueller Monat
        </span>
        <span className="flex items-center gap-1">
          <Check className="h-4 w-4 text-green-500" />
          Bezahlt
        </span>
        <span className="flex items-center gap-1">
          <Clock className="h-4 w-4 text-orange-500" />
          Offen
        </span>
        <span className="text-gray-400">
          Klicken Sie auf einen Monat für Details
        </span>
      </div>
    </div>
  );
}
